/**
 * 2D 引擎组件
 * 负责创建和管理第三方 2D 图纸引擎实例
 * 镜像 3D Engine 组件的设计模式
 */
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { themeManager } from '../../services/theme';
import type { Engine2dOptions, DrawingLoadOptions, Drawing2dLayer } from './types';
import type { ManagerRegistry } from '../../core/manager-registry';
// 导入第三方 SDK 的 createEngine2d 函数（从 npm 包引入）
import { createEngine2d as createEngine2dSDK } from 'iflow-engine-base';
import "../../../../bim_engine_base/dist/iflow-engine-base.css";

export type { Engine2dOptions, DrawingLoadOptions, Drawing2dLayer };

/**
 * 创建 Engine2d 实例的工厂函数
 * 兼容旧代码直接 import 的方式
 */
export const createEngine2d = (options: Engine2dOptions, registry: ManagerRegistry) => {
    return new Engine2d(options, registry);
};

/**
 * 2D 图纸引擎组件
 * 负责创建和管理第三方 2D 引擎实例
 */
export class Engine2d implements IBimComponent {
    /** 第三方 2D 引擎实例 */
    private engine: any = null;
    /** 管理器注册表实例 */
    private registry: ManagerRegistry;
    /** 引擎挂载的容器元素 */
    private container: HTMLElement;
    /** 引擎容器 ID（用于传递给 createEngine2d） */
    private containerId: string;
    /** 引擎配置选项（不包含 container） */
    private options: Omit<Engine2dOptions, 'container'>;
    /** 是否已初始化 */
    private _isInitialized = false;
    /** 是否已销毁 */
    private _isDestroyed = false;
    /** 主题订阅取消函数 */
    private unsubscribeTheme: (() => void) | null = null;

    /**
     * 构造函数
     * @param options 2D 引擎配置选项
     * @param registry 管理器注册表
     */
    constructor(options: Engine2dOptions, registry: ManagerRegistry) {
        // 保存注册表
        this.registry = registry;
        // 解析容器元素
        this.container = options.container;
        // 如果容器没有 id，生成一个唯一的 id
        if (!this.container.id) {
            this.containerId = `engine2d-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            this.container.id = this.containerId;
        } else {
            this.containerId = this.container.id;
        }

        // 保存配置选项（设置默认值）
        this.options = {
            backgroundColor: options.backgroundColor ?? 0x1a1a1a,
            gridEnabled: options.gridEnabled ?? true,
            axesEnabled: options.axesEnabled ?? true,
            selectionColor: options.selectionColor,
            highlightColor: options.highlightColor,
            enablePerformanceMonitoring: options.enablePerformanceMonitoring ?? true,
        };
    }

    /**
     * 初始化组件 (接口实现)
     * 创建 div 容器并初始化 2D 引擎
     */
    public init(): void {
        if (this._isInitialized) {
            console.warn('[Engine2d] Engine already initialized.');
            return;
        }

        if (this._isDestroyed) {
            console.error('[Engine2d] Cannot initialize destroyed engine.');
            return;
        }

        try {
            // 创建引擎配置对象
            const engineConfig: any = {
                containerId: this.containerId,
                backgroundColor: this.options.backgroundColor,
                gridEnabled: this.options.gridEnabled,
                axesEnabled: this.options.axesEnabled,
            };

            // 仅在用户提供时传递可选参数
            if (this.options.selectionColor !== undefined) {
                engineConfig.selectionColor = this.options.selectionColor;
            }
            if (this.options.highlightColor !== undefined) {
                engineConfig.highlightColor = this.options.highlightColor;
            }
            if (this.options.enablePerformanceMonitoring !== undefined) {
                engineConfig.enablePerformanceMonitoring = this.options.enablePerformanceMonitoring;
            }

            // 输出配置信息
            console.log('[Engine2d] 引擎配置信息：', engineConfig);

            // 调用引擎创建函数创建 2D 引擎实例
            this.engine = createEngine2dSDK(engineConfig);

            if (!this.engine) {
                throw new Error('Failed to create 2D engine instance');
            }

            // 标记为已初始化
            this._isInitialized = true;

            // 订阅主题变化
            this.unsubscribeTheme = themeManager.subscribe((theme) => {
                this.setTheme(theme);
            });

            // 应用当前主题
            this.setTheme(themeManager.getTheme());

            // 监听 2D 图纸实体点击事件
            if (this.engine.events) {
                this.engine.events.on('entity-click', (data: any) => {
                    console.log('[Engine2d] 实体点击:', data);
                    this.registry.emit('engine2d:entity-clicked', { data });
                });

                this.engine.events.on('layer-visibility-changed', (data: any) => {
                    console.log('[Engine2d] 图层可见性变更:', data);
                    this.registry.emit('engine2d:layer-changed', { data });
                });
            }

        } catch (error) {
            console.error('[Engine2d] Failed to initialize 2D engine:', error);
            this._isInitialized = false;
            throw error;
        }
    }

    /**
     * 设置主题 (接口实现)
     * @param _theme 全局主题配置
     */
    public setTheme(_theme: ThemeConfig): void {
        // 2D 引擎可根据主题切换深色/浅色模式
        // if (!this._isInitialized || !this.engine) return;

        // try {
        //     if (typeof this.engine.setTheme === 'function') {
        // 根据主题背景色判断深色/浅色
        //const isDark = _theme.bgBase ? this.isColorDark(_theme.bgBase) : false;
        //this.engine.setTheme(isDark ? 'dark' : 'light');
        //     }
        // } catch (e) {
        //     console.warn('[Engine2d] Failed to set theme:', e);
        // }
    }

    /**
     * 设置语言 (接口实现)
     */
    public setLocales(): void {
        // 2D 引擎组件暂时不需要本地化
    }

    /**
     * 检查是否已初始化
     */
    public isInitialized(): boolean {
        return this._isInitialized;
    }

    /**
     * 加载 2D 图纸
     * @param url 图纸文件 URL
     * @param options 加载选项
     */
    public async loadDrawing(url: string, options?: DrawingLoadOptions): Promise<void> {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine2d] Engine not initialized. Please call init() first.');
            return;
        }
        if (!url) {
            console.error('[Engine2d] Drawing URL is required.');
            return;
        }

        try {
            console.log('[Engine2d] 开始加载图纸:', url);
            await this.engine.loadModel(url, options);
            console.log('[Engine2d] 图纸加载完成:', url);
            this.registry.emit('engine2d:drawing-loaded', { url });
        } catch (error) {
            console.error('[Engine2d] 图纸加载失败:', error);
            throw error;
        }
    }

    /**
     * 获取所有图层
     * @returns 图层列表
     */
    public getLayers(): Drawing2dLayer[] {
        if (!this._isInitialized || !this.engine) {
            return [];
        }
        try {
            return this.engine.getLayers?.() ?? [];
        } catch (e) {
            console.warn('[Engine2d] Failed to get layers:', e);
            return [];
        }
    }

    /**
     * 设置图层可见性
     * @param name 图层名称
     * @param visible 是否可见
     */
    public setLayerVisible(name: string, visible: boolean): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine2d] Engine not initialized.');
            return;
        }
        try {
            this.engine.setLayerVisible?.(name, visible);
        } catch (e) {
            console.warn('[Engine2d] Failed to set layer visibility:', e);
        }
    }

    /**
     * 重置视图（适应画布）
     */
    public resetView(): void {
        if (!this._isInitialized || !this.engine) return;
        try {
            this.engine.resetView?.();
        } catch (e) {
            console.warn('[Engine2d] Failed to reset view:', e);
        }
    }

    /**
     * 适应视图（缩放到全部内容）
     */
    public fitToView(): void {
        if (!this._isInitialized || !this.engine) return;
        try {
            this.engine.fitToView?.();
        } catch (e) {
            console.warn('[Engine2d] Failed to fit to view:', e);
        }
    }

    /**
     * 设置缩放级别
     * @param zoom 缩放值
     */
    public setZoom(zoom: number): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.setZoom?.(zoom);
    }

    /**
     * 获取当前缩放级别
     * @returns 当前缩放值
     */
    public getZoom(): number {
        if (!this._isInitialized || !this.engine) return 1;
        return this.engine.getZoom?.() ?? 1;
    }

    /**
     * 设置背景色
     * @param color 颜色值（十六进制数值）
     */
    public setBackgroundColor(color: number): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.setBackgroundColor?.(color);
    }

    /**
     * 清除场景
     */
    public clearScene(): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.clearScene?.();
    }

    /**
     * 调整引擎尺寸
     */
    public resize(): void {
        if (!this._isInitialized || !this.engine) return;
        this.engine.resize?.();
    }

    /**
     * 订阅原始 2D 引擎事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public onRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.on(event, handler);
    }

    /**
     * 取消订阅原始 2D 引擎事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public offRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.off(event, handler);
    }

    /**
     * 销毁组件 (接口实现)
     * 清理资源、取消订阅、销毁引擎实例
     */
    public destroy(): void {
        if (this._isDestroyed) {
            return;
        }

        // 取消主题订阅
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }

        // 销毁 2D 引擎，释放资源
        if (this.engine) {
            try {
                this.engine.dispose?.();
            } catch (e) {
                console.warn('[Engine2d] Error during dispose:', e);
            }
            this.engine = null;
        }

        // 清理容器
        this.container.innerHTML = '';

        // 更新状态
        this._isDestroyed = true;
        this._isInitialized = false;
    }

    /**
     * 判断颜色字符串是否为深色
     * @param color CSS 颜色字符串
     * @returns 是否为深色
     */
    // private isColorDark(color: string): boolean {
    //     // 简单判断：如果包含常见深色关键字或数值较低则为深色
    //     if (color.includes('#')) {
    //         const hex = color.replace('#', '');
    //         const r = parseInt(hex.substring(0, 2), 16) || 0;
    //         const g = parseInt(hex.substring(2, 4), 16) || 0;
    //         const b = parseInt(hex.substring(4, 6), 16) || 0;
    //         const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    //         return luminance < 0.5;
    //     }
    //     return false;
    // }
}
